<template>
  <div>
    <AppStatCard :data="stats" v-bind="$attrs" card-type="stat" />
  </div>
</template>

<script>
import AppStatCard from "@elements/AppStatCard.vue";
import { flatMap, uniq } from "lodash-es";

export default {
  name: "EventParticipantsStats",
  components: { AppStatCard },
  props: {
    registrations: {
      type: Array,
      required: true
    },
    disciplineName: {
      type: String,
      default: null
    },
    includeUnregistered: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    stats() {
      const data = {
        title: this.$t(
          "common:statistics.heading",
          "Statistics",
          "Statistics box heading"
        ),
        subheaders: [],
        list: {
          athletes: {
            key: this.$t(
              "events:event.masterlist.Stat.Athletes.label",
              "Athletes"
            ),
            value: this.athletesCount
          },
          lungers: {
            key: this.$t(
              "events:event.masterlist.Stat.lungers.label",
              "Lungers"
            ),
            value: this.lungersCount
          },
          horses: {
            key: this.$t("events:event.masterlist.Stat.horses.label", "Horses"),
            value: this.horsesCount
          },
          horseSlots: {
            key: this.$t(
              "events:event.masterlist.Stat.horseSlots.label",
              "Horse slots"
            ),
            value: this.horseSlotsCount
          },
          competitionPair: {
            key: this.$t(
              "events:event.masterlist.Stat.Pairs.label",
              "Competition pairs"
            ),
            value: this.pairsCount
          },
          starts: {
            key: this.$t(
              "events:event.masterlist.Stat.Starts.label",
              "Starts, incl late entries"
            ),
            value: this.startsCount
          },
          lateEntries: {
            key: this.$t(
              "events:event.masterlist.Stat.LateEntries.label",
              "Late entries"
            ),
            value: this.lateEntries
          },
          unregistered: {
            key: this.$t(
              "events:event.masterlist.Stat.unregistered.label",
              "Unregistered"
            ),
            value: this.unregisteredCount
          },
          paidEntries: {
            key: this.$t(
              "events:event.masterlist.Stat.paidEntries.label",
              "Paid Entries"
            ),
            value: this.paidEntriesCount
          }
        }
      };
      if (this.disciplineName != "vaulting") delete data.list.lungers;
      if (!this.includeUnregistered) delete data.list.unregistered;

      if (this.horseSlotsCount == 0) delete data.list.horseSlots;
      return data;
    },
    filteredRegs() {
      return JSON.parse(JSON.stringify(this.registrations)).reduce(
        (regs, reg) => {
          if (!this.includeUnregistered) {
            reg.entries = reg.entries.filter(
              entry => entry && entry.status != "unregistered"
            );
          }
          if (reg.entries.length == 0) {
            return regs;
          }
          regs.push(reg);
          return regs;
        },
        []
      );
    },

    lungersCount() {
      // Return unique lungers total amount
      return uniq(
        this.filteredRegs.map(({ vaultingLungerPerson: p }) => p?.id)
      ).filter(Boolean).length;
    },
    athletesCount() {
      // Return unique athletes total amount
      return uniq(flatMap(this.filteredRegs, i => i.athletes.map(a => a.id)))
        .length;
    },
    horsesCount() {
      // Return unique athletes total amount
      return uniq(
        flatMap(this.filteredRegs, i =>
          i.horses.filter(x => !x.isHorseSlot).map(h => h.id)
        )
      ).length;
    },
    horseSlotsCount() {
      // Return unique athletes total amount
      return uniq(
        flatMap(this.filteredRegs, i =>
          i.horses.filter(x => x.isHorseSlot).map(h => h.id)
        )
      ).length;
    },
    startsCount() {
      return this.filteredRegs.reduce((count, { entries: e }) => {
        e.forEach(({ status }) => count++);
        return count;
      }, 0);
    },
    lateEntries() {
      return this.filteredRegs.reduce((count, { entries: e }) => {
        e.forEach(({ isLateEntry }) => (isLateEntry ? count++ : ""));
        return count;
      }, 0);
    },
    unregisteredCount() {
      return this.registrations.reduce((count, { entries: e }) => {
        e.forEach(entry => (entry.status == "unregistered" ? count++ : ""));
        return count;
      }, 0);
    },
    paidEntriesCount() {
      return this.registrations.reduce((count, { entries: e }) => {
        e.forEach(entry => (entry.paymentStatus === "PAID" ? count++ : ""));
        return count;
      }, 0);
    },
    pairsCount() {
      return this.filteredRegs.filter(x => x.horses.every(y => !y.isHorseSlot))
        .length;
    }
  }
};
</script>
